import {
  JOURNEY_ENTRY_MAX_ROWS,
  zonedInstant,
  type JourneyBookEntry,
  type JourneyEntryInputDraft,
} from '@jupi/shared'

// What the entry dialog edits, and how it becomes the API's input. Kept out
// of the component so it is tested without a browser (journeyEntryForm.test.ts).

export interface EntryDraft {
  kind: 'entry' | 'blank'
  // The position in the book, as the club's wall clock.
  date: string
  time: string
  text: string
  enteredByText: string
  maintenanceRecordId: number | null
  rowCount: number
  blankBefore: number
  blankAfter: number
}

// "2026-05-26" and "07:37" in `timeZone`, for an instant.
export function clubDateTime(instant: string | Date, timeZone: string): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(instant))
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}` }
}

export function newDraft(now: Date, timeZone: string, enteredBy: string): EntryDraft {
  return {
    kind: 'entry',
    ...clubDateTime(now, timeZone),
    text: '',
    enteredByText: enteredBy,
    maintenanceRecordId: null,
    rowCount: 1,
    blankBefore: 0,
    blankAfter: 0,
  }
}

export function draftOf(entry: JourneyBookEntry, timeZone: string): EntryDraft {
  return {
    kind: entry.kind,
    ...clubDateTime(entry.occurredAt, timeZone),
    text: entry.text ?? '',
    enteredByText: entry.enteredByText ?? '',
    maintenanceRecordId: entry.maintenanceRecordId,
    rowCount: entry.rowCount,
    blankBefore: 0,
    blankAfter: 0,
  }
}

const DATE = /^\d{4}-\d{2}-\d{2}$/
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/

const inRows = (n: number, min: number, max: number) => Number.isInteger(n) && n >= min && n <= max

/** Whether the draft can be saved: what the dialog enables its button on. */
export function draftProblem(draft: EntryDraft, rowsPerSpread: number | null): string | null {
  const longest = Math.min(JOURNEY_ENTRY_MAX_ROWS, rowsPerSpread ?? JOURNEY_ENTRY_MAX_ROWS)
  if (!DATE.test(draft.date) || !TIME.test(draft.time)) return 'when'
  if (!inRows(draft.rowCount, 1, longest)) return 'rows'
  if (draft.kind === 'entry') {
    if (draft.text.trim() === '') return 'text'
    if (!inRows(draft.blankBefore, 0, longest) || !inRows(draft.blankAfter, 0, longest)) return 'rows'
  }
  return null
}

/** The API's input for a draft, or null while it is not valid. */
export function toInput(draft: EntryDraft, timeZone: string, rowsPerSpread: number | null): JourneyEntryInputDraft | null {
  if (draftProblem(draft, rowsPerSpread)) return null
  const occurredAt = zonedInstant(draft.date, draft.time, timeZone)
  if (draft.kind === 'blank') return { kind: 'blank', occurredAt, rowCount: draft.rowCount }
  return {
    kind: 'entry',
    occurredAt,
    rowCount: draft.rowCount,
    text: draft.text.trim(),
    enteredByText: draft.enteredByText.trim() || null,
    maintenanceRecordId: draft.maintenanceRecordId,
    blankBefore: draft.blankBefore,
    blankAfter: draft.blankAfter,
  }
}
